import React, { useEffect, useState } from "react";
import { X, Target, Zap, Bell } from "lucide-react";
import { pushSupported, enablePush } from "@/lib/push";
import { toast } from "sonner";

const STORAGE_KEY = "rivendas_onboarding_done";

const STEPS = [
  {
    icon: Target,
    title: "Escolha o que você quer fazer",
    body: "Comprar, vender, permutar, doar ou contratar serviço — tudo na mesma tela, com um toque.",
  },
  {
    icon: Zap,
    title: "Publique seu pedido em segundos",
    body: "Você diz o quê, quanto e onde. Fornecedores compatíveis recebem em tempo real.",
  },
  {
    icon: Bell,
    title: "Ative avisos rápidos",
    body: "Um ping discreto assim que alguém responde ao seu pedido. Nunca perca uma oportunidade.",
    cta: "Ativar notificações",
  },
];

/**
 * OnboardingTour — 3-step welcome modal that shows only on the FIRST visit
 * of a logged-in user. Persists dismissal in localStorage. Last step lets
 * the user turn on Web Push in one click.
 */
export default function OnboardingTour({ user }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch { /* ignore */ }
    // Delay slightly so it doesn't clash with cold-start splash
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, [user]);

  const finish = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  const next = async () => {
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
      return;
    }
    // Last step — try to enable push
    if (pushSupported()) {
      setBusy(true);
      try {
        await enablePush();
        toast.success("Notificações ativadas");
      } catch (e) {
        toast.error(e?.message || "Você pode ativar depois em Perfil");
      } finally {
        setBusy(false);
      }
    }
    finish();
  };

  if (!visible || !user) return null;
  const current = STEPS[step];
  const I = current.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div
      data-testid="onboarding-tour"
      className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
    >
      <div className="w-full max-w-md bg-card border border-border rounded-2xl overflow-hidden shadow-2xl shadow-primary/10">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-6 bg-primary" : i < step ? "w-6 bg-primary/40" : "w-2 bg-muted"
                }`}
              />
            ))}
          </div>
          <button
            data-testid="onboarding-close"
            onClick={finish}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6 md:p-8">
          <div className="w-14 h-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center mb-4">
            <I size={26} />
          </div>
          <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight mb-2">
            {current.title}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {current.body}
          </p>
          <div className="mt-6 flex items-center gap-2">
            <button
              data-testid="onboarding-skip"
              onClick={finish}
              className="text-xs text-muted-foreground hover:text-foreground px-3 py-2"
            >
              Pular
            </button>
            <button
              data-testid="onboarding-next"
              onClick={next}
              disabled={busy}
              className="ml-auto text-sm font-bold px-5 py-2.5 rounded-lg bg-primary text-primary-foreground disabled:opacity-60"
            >
              {busy ? "..." : isLast ? (current.cta || "Concluir") : "Próximo"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
